export const colors = {
  primary: 'var(--voyager-color-primary, #440f95)',
  tertiary: 'var(--voyager-color-tertiary, #f1f1f1)',

  navigationAndFooter: 'var(--voyager-color-navigationAndFooter, #333333)',
  header: 'var(--voyager-color-header, #F4F4F4)',
  sectionTitle: 'var(--voyager-color-sectionTitle, #919191)',

  black: 'var(--voyager-color-black, #0d0d0d)',
  white: 'var(--voyager-color-white, #FFFFFF)',
  offWhite: 'var(--voyager-color-offWhite, #f1f1f1)',
} as const;

export const space = {
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '24px',
  6: '32px',
  7: '40px',
  8: '48px',
  9: '64px',
  10: '80px',
  11: '96px',
  '2px': '2px',
  '20px': '20px',
  '36px': '36px',
  '56px': '56px',
  '120px': '120px',
} as const;

export const fonts = {
  heading: "var(--voyager-heading-font, 'PP Eiko', 'Playfair Display'), serif",
  body: "var(--voyager-body-font, 'Suisse Intl', 'Inter'), sans-serif",
  baseSize: '16px',
  size: {
    xxs: '0.5625rem', // 9px
    xs: '0.8125rem', // 13px
    sm: '0.875rem', // 14px
    md: '0.9375rem', // 15px
    base: '1rem', // 16px
    lg: '1.25rem', // 20px
    xl: '1.5rem', // 24px
    '2xl': '1.75rem', // 28px
    '3xl': '2.5rem', // 40px
    '4xl': '3rem', // 48px
  },
  weight: {
    normal: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
  },
} as const;

export const headingFont = fonts.heading;
export const bodyFont = fonts.body;

export const breakpoints = {
  mobile: '576px',
  tablet: '768px',
  desktop: '992px',
  wide: '1200px',
} as const;

export const layout = {
  maxWidth: '1480px',
  narrowMaxWidth: '860px',
  horizontalPadding: '20px',
  headerHeightMobile: '81px',
  headerHeightDesktop: '111px',
  mobileNavHeight: '64px',
  sideNavWidth: '72px',
  heroImageMobileHeight: '260px',
  desktopBreakpoint: '769px',
  inputPadding: '10px 12px',
  borderRadius: '4px',
  borderRadiusMd: '8px',
  borderRadiusLg: '12px',
  borderRadiusXl: '16px',
  borderRadius2xl: '24px',
  borderRadius3xl: '32px',
  borderRadiusFull: '9999px',
  zIndexDropdown: 1000,
  zIndexModal: 10000,
  modalWidth: '420px',
  modalWidthLg: '600px',
} as const;

export const blur = {
  sm: '16px',
  lg: '40px',
} as const;

export const depth = {
  xs: '4px',
} as const;

export const shadow = {
  sm: '0 1px 2px 0 rgba(13, 13, 13, 0.08), 0 1px 3px 0 rgba(13, 13, 13, 0.06)',
  lg: '0 8px 24px -4px rgba(13, 13, 13, 0.16), 0 4px 8px -4px rgba(13, 13, 13, 0.08)',
} as const;

export const primitives = {
  color: {
    blackAlpha: {
      50: '#0d0d0d0d',
      100: '#0d0d0d1a',
      200: '#0d0d0d33',
      400: '#0d0d0d66',
      600: '#0d0d0d99',
      1000: '#0d0d0d',
    },
    whiteAlpha: {
      50: '#ffffff0d',
      100: '#ffffff1a',
      400: '#ffffff66',
      1000: '#ffffff',
    },
  },
  typography: {
    fontFamily: {
      brandPrimary: "var(--voyager-heading-font, 'PP Eiko', 'Playfair Display'), serif",
      brandSecondary: "var(--voyager-body-font, 'Suisse Intl', 'Inter'), sans-serif",
    },
    fontWeight: {
      regular: 400,
      medium: 500,
    },
    tracking: {
      tightest: '-1px',
      tight: '-0.5px',
      normal: '0',
      wide: '0.5px',
    },
    lineHeight: {
      tight: 1.2,
      snug: 1.25,
      normal: 1.4,
      relaxed: 1.5,
      loose: 1.6,
    },
  },
} as const;

export const semantic = {
  text: {
    primary: 'var(--voyager-text-primary, #0d0d0d)',
    secondary: 'var(--voyager-text-secondary, #0d0d0d99)',
    tertiary: 'var(--voyager-text-tertiary, #0d0d0d66)',
    inverse: 'var(--voyager-text-inverse, #ffffff)',
    disabled: 'var(--voyager-text-disabled, #0d0d0d1a)',
    danger: 'var(--voyager-text-danger, #be0e2c)',
    positive: 'var(--voyager-text-positive, #178c3c)',
    info: 'var(--voyager-text-info, #184cb4)',
    warning: 'var(--voyager-text-warning, #8a4b00)',
  },
  bg: {
    primary: 'var(--voyager-bg-primary, #0d0d0d)',
    secondary: 'var(--voyager-bg-secondary, #ffffff)',
    canvas: 'var(--voyager-bg-canvas, #ffffff)',
    brand: 'var(--voyager-bg-brand, var(--voyager-color-primary, #440f95))',
    surfaceSubtle: 'var(--voyager-bg-surface-subtle, #0d0d0d0d)',
    surfaceStrong: 'var(--voyager-bg-surface-strong, #0d0d0d1a)',
    overlayBlur: 'var(--voyager-bg-overlay-blur, #ffffff0d)',
    overlayBlurStrong: `var(--voyager-bg-overlay-blur-strong, ${primitives.color.whiteAlpha[400]})`,
    overlayImage: 'var(--voyager-bg-overlay-image, #0d0d0d1a)',
    overlayModal: 'var(--voyager-bg-overlay-modal, #0d0d0d33)',
    infoSubtle: 'var(--voyager-bg-info-subtle, #e6f0fe)',
    infoStrong: 'var(--voyager-bg-info-strong, #184cb4)',
    positiveSubtle: 'var(--voyager-bg-positive-subtle, #e7fded)',
    positiveStrong: 'var(--voyager-bg-positive-strong, #0c6e2a)',
    dangerSubtle: 'var(--voyager-bg-danger-subtle, #fee6e9)',
    dangerStrong: 'var(--voyager-bg-danger-strong, #be0e2c)',
    warningSubtle: 'var(--voyager-bg-warning-subtle, #fff4e0)',
    warningStrong: 'var(--voyager-bg-warning-strong, #a15a00)',
    urgencyStrong: 'var(--voyager-bg-urgency-strong, #980b23)',
  },
  border: {
    default: 'var(--voyager-border-default, #0d0d0d1a)',
    subtle: 'var(--voyager-border-subtle, #0d0d0d0d)',
    inverse: 'var(--voyager-border-inverse, #ffffff)',
    selected: 'var(--voyager-border-selected, #0d0d0d)',
    danger: 'var(--voyager-border-danger, #980b23)',
    positive: 'var(--voyager-border-positive, #0c6e2a)',
    warning: 'var(--voyager-border-warning, #a15a00)',
    info: 'var(--voyager-border-info, #184cb4)',
  },
  icon: {
    primary: 'var(--voyager-icon-primary, #0d0d0d)',
    secondary: 'var(--voyager-icon-secondary, #0d0d0d99)',
    inverse: 'var(--voyager-icon-inverse, #ffffff)',
    disabled: 'var(--voyager-icon-disabled, #0d0d0d1a)',
    danger: 'var(--voyager-icon-danger, #be0e2c)',
    info: 'var(--voyager-icon-info, #184cb4)',
  },
} as const;

export const component = {
  button: {
    primary: {
      enabled: {
        bg: semantic.bg.brand,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      hover: {
        bg: `color-mix(in srgb, ${semantic.bg.brand} 60%, ${colors.black})`,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      focussed: {
        bg: `color-mix(in srgb, ${semantic.bg.brand} 90%, ${colors.white})`,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      pressed: {
        bg: `color-mix(in srgb, ${semantic.bg.brand} 60%, ${colors.black})`,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      disabled: {
        bg: semantic.bg.surfaceSubtle,
        text: semantic.text.disabled,
        icon: semantic.icon.disabled,
      },
    },
    danger: {
      enabled: {
        bg: semantic.bg.urgencyStrong,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      hover: {
        bg: `color-mix(in srgb, ${semantic.bg.urgencyStrong} 95%, ${colors.black})`,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      focussed: {
        bg: `color-mix(in srgb, ${semantic.bg.urgencyStrong} 95%, ${colors.white})`,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      pressed: {
        bg: `color-mix(in srgb, ${semantic.bg.urgencyStrong} 60%, ${colors.white})`,
        text: semantic.text.inverse,
        icon: semantic.icon.inverse,
      },
      disabled: {
        bg: semantic.bg.surfaceSubtle,
        text: semantic.text.disabled,
        icon: semantic.icon.disabled,
      },
    },
  },
} as const;

const brandHeadingFamily = primitives.typography.fontFamily.brandPrimary;
const brandBodyFamily = primitives.typography.fontFamily.brandSecondary;

export const textStyle = {
  display: {
    xl: {
      fontFamily: brandHeadingFamily,
      fontSize: '3rem',
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    lg: {
      fontFamily: brandHeadingFamily,
      fontSize: '2.5rem',
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    md: {
      fontFamily: brandHeadingFamily,
      fontSize: '2rem',
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    sm: {
      fontFamily: brandHeadingFamily,
      fontSize: '1.5rem',
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
  },
  headline: {
    xl: {
      fontFamily: brandHeadingFamily,
      fontSize: '1.5rem',
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    lg: {
      fontFamily: brandHeadingFamily,
      fontSize: '1.25rem',
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    md: {
      fontFamily: brandHeadingFamily,
      fontSize: '1.125rem',
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
    sm: {
      fontFamily: brandHeadingFamily,
      fontSize: '1rem',
      fontWeight: fonts.weight.normal,
      lineHeight: primitives.typography.lineHeight.tight,
      letterSpacing: primitives.typography.tracking.tight,
    },
  },
  body: {
    lg: {
      regular: {
        fontFamily: brandBodyFamily,
        fontSize: '1rem',
        fontWeight: fonts.weight.normal,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
      strong: {
        fontFamily: brandBodyFamily,
        fontSize: '1rem',
        fontWeight: fonts.weight.medium,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
    },
    md: {
      regular: {
        fontFamily: brandBodyFamily,
        fontSize: '0.875rem',
        fontWeight: fonts.weight.normal,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
      strong: {
        fontFamily: brandBodyFamily,
        fontSize: '0.875rem',
        fontWeight: fonts.weight.medium,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
    },
    sm: {
      regular: {
        fontFamily: brandBodyFamily,
        fontSize: '0.75rem',
        fontWeight: fonts.weight.normal,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
      strong: {
        fontFamily: brandBodyFamily,
        fontSize: '0.75rem',
        fontWeight: fonts.weight.medium,
        lineHeight: primitives.typography.lineHeight.normal,
        letterSpacing: primitives.typography.tracking.normal,
      },
    },
  },
} as const;

export const colorVarNames = {
  primary: '--voyager-color-primary',
  tertiary: '--voyager-color-tertiary',
  navigationAndFooter: '--voyager-color-navigationAndFooter',
  header: '--voyager-color-header',
  sectionTitle: '--voyager-color-sectionTitle',
  black: '--voyager-color-black',
  white: '--voyager-color-white',
  offWhite: '--voyager-color-offWhite',
} as const;

export const fontVarNames = {
  heading: '--voyager-heading-font',
  body: '--voyager-body-font',
} as const;
